import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { CLIENT_ID } from '../../lib/client-context';
import { listKeys } from './listKeys';
import {
  ListItemData,
  FormattedListItem,
  CreateListItemInput,
  UpdateListItemInput,
  formatListItem,
  sortByOrderIndex,
  getMaxOrderIndex,
} from './listTypes';
import { db } from '../../lib/offline-db';
import { useConnectionState } from '../useConnectionState';
import { useQueryErrorHandler } from '../useQueryErrorHandler';

/**
 * Hook for fetching all list items for a specific topic
 * Supports offline mode by using IndexedDB when offline
 */
export function useListItems(topicId: string | null) {
  const { isOnline } = useConnectionState();
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: topicId ? listKeys.byTopic(topicId) : listKeys.lists(),
    queryFn: async () => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(
        `Starting list items fetch #${fetchId} for topic ${topicId} (online: ${isOnline})`
      );
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching list items from IndexedDB #${fetchId}`);

          try {
            // Get list items from IndexedDB
            const listItems = await db.listItems
              .where('topic_id')
              .equals(topicId)
              .and(item => !item.isDeleted) // Filter out deleted items
              .toArray();

            logger.fetch(`Offline mode: loaded ${listItems.length} list items from IndexedDB`);

            // Sort by order_index
            const sortedItems = sortByOrderIndex(listItems as ListItemData[]);

            // Format items for UI
            return sortedItems.map(item => formatListItem(item as ListItemData));
          } catch (offlineError) {
            logger.error(`Error fetching offline list items #${fetchId}:`, offlineError);
            throw new Error('Failed to load list items from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('list_items')
          .select('*')
          .eq('topic_id', topicId)
          .order('order_index', { ascending: true });

        if (error) throw error;

        // Store in IndexedDB for offline access
        if (data && data.length > 0) {
          try {
            await db.listItems.bulkPut(data);
            logger.fetch(`Stored ${data.length} list items in IndexedDB`);
          } catch (storageError) {
            logger.error(`Error storing list items in IndexedDB:`, storageError);
            // Continue even if storage fails - this is non-critical
          }
        }

        // Format list items using the helper function
        const formattedItems = data?.map(item => formatListItem(item as ListItemData)) || [];

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`List items fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

        return formattedItems;
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'toast',
          context: {
            operation: 'fetchListItems',
            topicId,
            fetchId,
            isOnline,
          },
        });
        throw appError;
      }
    },
    enabled: !!topicId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for creating a new list item with optimistic updates
 */
export function useAddListItem() {
  const queryClient = useQueryClient();
  const { isOnline } = useConnectionState();
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (input: CreateListItemInput): Promise<FormattedListItem> => {
      const { topicId, text } = input;

      logger.store('Adding new list item', {
        topicId,
        text,
        is_online: isOnline,
      });

      try {
        const supabaseClient = await supabase;
        const {
          data: { user },
        } = await supabaseClient.auth.getUser();

        if (!user) {
          throw new Error('No authenticated user');
        }

        // Get the current max order_index for this topic
        let maxOrderIndex = 0;

        if (isOnline) {
          const { data: maxOrderIndexData, error: maxOrderIndexError } = await supabaseClient
            .from('list_items')
            .select('order_index')
            .eq('topic_id', topicId)
            .order('order_index', { ascending: false })
            .limit(1)
            .single();

          if (!maxOrderIndexError) {
            maxOrderIndex = maxOrderIndexData
              ? parseFloat(String(maxOrderIndexData.order_index)) + 1
              : 0;
          } else if (maxOrderIndexError.code !== 'PGRST116') {
            // PGRST116 is the "no rows returned" error, which is expected if there are no items
            logger.error('Error fetching max order_index:', maxOrderIndexError);
          }
        } else {
          // For offline mode, get max order_index from IndexedDB
          const items = await db.listItems.where('topic_id').equals(topicId).toArray();

          maxOrderIndex = getMaxOrderIndex(items as ListItemData[]) + 1;
        }

        // Create item data object with metadata
        const now = new Date().toISOString();
        const itemId = crypto.randomUUID();

        const listItemData: ListItemData = {
          id: itemId,
          topic_id: topicId,
          user_id: user.id,
          text: text.trim(),
          order_index: maxOrderIndex,
          created_at: now,
          updated_at: now,
          metadata: {
            client_id: CLIENT_ID,
            last_update: now,
          },
        };

        // If offline, save locally and queue for sync
        if (!isOnline) {
          // Store in IndexedDB
          await db.listItems.put(listItemData);

          // Queue for sync when online - not implemented yet
          // await queueOfflineOperation('list_items', 'create', itemId, listItemData);

          // Return formatted list item
          return formatListItem(listItemData);
        }

        // Online flow - send to server
        const { data: newItem, error } = await supabaseClient
          .from('list_items')
          .insert([listItemData])
          .select()
          .single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.listItems.put(newItem);
          logger.fetch(`Stored list item ${newItem.id} in IndexedDB`);
        } catch (storageError) {
          logger.error(`Error storing list item in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Return formatted list item
        return formatListItem(newItem as ListItemData);
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'toast',
          context: {
            operation: 'addListItem',
            topicId,
            text: text.substring(0, 50),
            isOnline,
          },
        });
        throw appError;
      }
    },

    // Optimistic update - immediately show the new item
    onMutate: async (input: CreateListItemInput) => {
      const { topicId, text } = input;

      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: listKeys.byTopic(topicId) });

      // Get current data
      const previousItems = queryClient.getQueryData<FormattedListItem[]>(
        listKeys.byTopic(topicId)
      );

      // Create optimistic item
      const optimisticItem: FormattedListItem = {
        id: `temp-${Date.now()}`, // Temporary ID until server responds
        topicId,
        userId: 'temp-user', // Will be replaced by server
        text: text.trim(),
        orderIndex: getMaxOrderIndex(previousItems || []) + 1,
        isOptimistic: true, // Mark as optimistic
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistically update the cache
      queryClient.setQueryData<FormattedListItem[]>(listKeys.byTopic(topicId), (oldData = []) =>
        sortByOrderIndex([...oldData, optimisticItem])
      );

      // Return context to use in onError/onSettled
      return { previousItems, optimisticItem };
    },

    // If mutation fails, rollback to previous data
    onError: (error, variables, context) => {
      const { topicId } = variables;
      if (context?.previousItems) {
        queryClient.setQueryData(listKeys.byTopic(topicId), context.previousItems);
      }
    },

    // Replace optimistic item with real server data
    onSuccess: (newItem, variables, context) => {
      const { topicId } = variables;

      // Replace the optimistic item with the real one from server
      queryClient.setQueryData<FormattedListItem[]>(listKeys.byTopic(topicId), (oldData = []) => {
        // Remove the optimistic item and add the real one
        const filteredData = oldData.filter(item => item.id !== context?.optimisticItem.id);
        return sortByOrderIndex([...filteredData, newItem]);
      });
    },
  });
}

/**
 * Hook for updating a list item with optimistic updates
 */
export function useUpdateListItem() {
  const queryClient = useQueryClient();
  const { isOnline } = useConnectionState();
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      topicId,
      itemId,
      updates,
    }: {
      topicId: string;
      itemId: string;
      updates: UpdateListItemInput;
    }): Promise<FormattedListItem> => {
      if (!itemId) {
        throw new Error('List item ID is required');
      }

      const now = new Date().toISOString();

      // Create update payload
      const updatePayload: Partial<ListItemData> = {
        text: updates.text.trim(),
        updated_at: now,
        metadata: {
          client_id: CLIENT_ID,
          last_update: now,
        },
      };

      logger.store('Updating list item', {
        itemId,
        topicId,
        is_online: isOnline,
      });

      // If offline, update locally and queue for sync
      if (!isOnline) {
        try {
          // Get current list item from IndexedDB
          const currentItem = await db.listItems.get(itemId);

          if (!currentItem || currentItem.isDeleted) {
            throw new Error(`List item ${itemId} not found in offline storage`);
          }

          // Merge updates with current data
          const updatedItem = {
            ...currentItem,
            ...updatePayload,
          };

          // Update in IndexedDB
          await db.listItems.put(updatedItem);

          // Queue for sync when online - not implemented yet
          // await queueOfflineOperation('list_items', 'update', itemId, updatedItem);

          // Return formatted list item
          return formatListItem(updatedItem as ListItemData);
        } catch (offlineError) {
          logger.error('Error updating list item offline:', offlineError);
          handleError(offlineError, {
            action: 'toast',
            context: {
              operation: 'updateListItem',
              itemId,
              topicId,
              mode: 'offline',
              isOnline,
            },
          });
          throw offlineError;
        }
      }

      // Online flow - update on server
      try {
        const supabaseClient = await supabase;

        // Get current item to access its metadata and preserve existing metadata fields
        const { data: currentItem, error: fetchError } = await supabaseClient
          .from('list_items')
          .select('metadata')
          .eq('id', itemId)
          .single();

        if (fetchError) throw fetchError;

        // Preserve existing metadata fields
        if (currentItem?.metadata) {
          updatePayload.metadata = {
            ...currentItem.metadata,
            ...updatePayload.metadata,
          };
        }

        // Update the list item
        const { data, error } = await supabaseClient
          .from('list_items')
          .update(updatePayload)
          .eq('id', itemId)
          .select()
          .single();

        if (error) throw error;

        // Re-fetch all items to ensure consistent ordering
        const { data: allItems, error: fetchAllError } = await supabaseClient
          .from('list_items')
          .select('*')
          .eq('topic_id', topicId)
          .order('order_index', { ascending: true });

        if (fetchAllError) throw fetchAllError;

        // Update in IndexedDB for offline access
        try {
          // Get current data first to preserve fields not included in the update
          const existingItem = await db.listItems.get(itemId);

          if (existingItem) {
            await db.listItems.put({
              ...existingItem,
              ...data,
            });

            // Update the full list in IndexedDB for consistent state
            if (allItems && allItems.length > 0) {
              await db.listItems.bulkPut(allItems);
            }

            logger.fetch(`Updated list item ${itemId} in IndexedDB`);
          }
        } catch (storageError) {
          logger.error(`Error updating list item in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format and return the list item
        return formatListItem(data as ListItemData);
      } catch (error) {
        // Log the error and rethrow
        logger.error('Error updating list item:', error);
        handleError(error, {
          action: 'toast',
          context: {
            operation: 'updateListItem',
            itemId,
            topicId,
            isOnline,
          },
        });
        throw error;
      }
    },

    // Optimistic update - immediately show the updated item
    onMutate: async ({ topicId, itemId, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: listKeys.byTopic(topicId) });

      // Get current data
      const previousItems = queryClient.getQueryData<FormattedListItem[]>(
        listKeys.byTopic(topicId)
      );
      const previousItem = previousItems?.find(item => item.id === itemId);

      if (!previousItem) {
        throw new Error('Item not found for optimistic update');
      }

      // Create optimistically updated item
      const optimisticItem: FormattedListItem = {
        ...previousItem,
        text: updates.text.trim(),
        updatedAt: new Date().toISOString(),
        isOptimistic: true, // Mark as optimistic
      };

      // Optimistically update the cache
      queryClient.setQueryData<FormattedListItem[]>(listKeys.byTopic(topicId), (oldData = []) => {
        const updatedItems = oldData.map(item => (item.id === itemId ? optimisticItem : item));
        return sortByOrderIndex(updatedItems);
      });

      // Return context for rollback
      return { previousItems, previousItem };
    },

    // If mutation fails, rollback to previous data
    onError: (error, variables, context) => {
      const { topicId } = variables;
      if (context?.previousItems) {
        queryClient.setQueryData(listKeys.byTopic(topicId), context.previousItems);
      }
    },

    // Replace optimistic update with real server data
    onSuccess: (updatedItem, variables) => {
      const { topicId, itemId } = variables;

      // Update list item in the QueryClient cache with server data
      queryClient.setQueryData<FormattedListItem[]>(listKeys.byTopic(topicId), (oldData = []) => {
        // Replace the optimistic item with real server data
        const updatedItems = oldData.map(item => (item.id === itemId ? updatedItem : item));
        return sortByOrderIndex(updatedItems);
      });
    },
  });
}

/**
 * Hook for deleting a list item with optimistic updates
 */
export function useDeleteListItem() {
  const queryClient = useQueryClient();
  const { isOnline } = useConnectionState();
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ topicId, itemId }: { topicId: string; itemId: string }) => {
      try {
        logger.store('Deleting list item', {
          itemId,
          topicId,
          is_online: isOnline,
        });

        // If offline, mark as deleted locally and queue for sync
        if (!isOnline) {
          logger.store('Offline mode: Marking list item as deleted locally', { itemId });

          try {
            // Mark as deleted in IndexedDB
            await db.listItems.update(itemId, { isDeleted: true });

            // Queue for sync when online - not implemented yet
            // await queueOfflineOperation('list_items', 'delete', itemId, { id: itemId });

            return { itemId, topicId };
          } catch (offlineError) {
            logger.error('Error deleting list item offline:', offlineError);
            handleError(offlineError, {
              action: 'toast',
              context: {
                operation: 'deleteListItem',
                itemId,
                topicId,
                mode: 'offline',
                isOnline,
              },
            });
            throw offlineError;
          }
        }

        // Online flow - delete from server
        const { error: deleteError } = await supabase.from('list_items').delete().eq('id', itemId);

        if (deleteError) throw deleteError;

        // Re-fetch all items to ensure consistent ordering
        const { data: remainingItems, error: fetchError } = await supabase
          .from('list_items')
          .select('*')
          .eq('topic_id', topicId)
          .order('order_index', { ascending: true });

        if (fetchError) throw fetchError;

        // Update IndexedDB
        try {
          // Delete the item from IndexedDB
          await db.listItems.delete(itemId);

          // Update the remaining items in IndexedDB for consistent state
          if (remainingItems && remainingItems.length > 0) {
            await db.listItems.bulkPut(remainingItems);
          }

          logger.fetch(`Deleted list item ${itemId} from IndexedDB and updated remaining items`);
        } catch (storageError) {
          logger.error(`Error updating IndexedDB after deletion:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return { itemId, topicId };
      } catch (error) {
        // Log the error and rethrow
        logger.error('Error deleting list item:', error);
        handleError(error, {
          action: 'toast',
          context: {
            operation: 'deleteListItem',
            itemId,
            topicId,
            isOnline,
          },
        });
        throw error;
      }
    },

    // Optimistic update - immediately remove the item
    onMutate: async ({ topicId, itemId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: listKeys.byTopic(topicId) });

      // Get current data
      const previousItems = queryClient.getQueryData<FormattedListItem[]>(
        listKeys.byTopic(topicId)
      );

      // Optimistically remove the item from cache
      queryClient.setQueryData<FormattedListItem[]>(listKeys.byTopic(topicId), (oldData = []) =>
        oldData.filter(item => item.id !== itemId)
      );

      // Return context for rollback
      return { previousItems };
    },

    // If mutation fails, rollback to previous data
    onError: (error, variables, context) => {
      const { topicId } = variables;
      if (context?.previousItems) {
        queryClient.setQueryData(listKeys.byTopic(topicId), context.previousItems);
      }
    },

    // Confirm deletion succeeded (cache already updated optimistically)
    onSuccess: result => {
      const { itemId, topicId } = result;

      // Cache is already updated optimistically, but we can invalidate to ensure consistency
      // This is optional since we already removed the item optimistically
      logger.store('List item deleted successfully', { itemId, topicId });
    },
  });
}
